"use client";

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import type { Socket } from "socket.io-client";
import { getSocketClient, getSocketServerUrl } from "@/lib/socket/client";
import { logger } from "@/lib/logger";
import { getSession } from "@/lib/session";

type SocketContextValue = {
  socket: Socket | null;
  isConnected: boolean;
  isEnabled: boolean;
};

const SocketContext = createContext<SocketContextValue | null>(null);

/**
 * SocketProvider
 * @param param0 children
 * @returns SocketProvider
 */
export const SocketProvider = ({ children }: { children: ReactNode }) => {
  const [socket] = useState<Socket | null>(() => getSocketClient());
  const [isConnected, setIsConnected] = useState<boolean>(
    () => socket?.connected ?? false,
  );
  const isEnabled = Boolean(getSocketServerUrl());

  useEffect(() => {
    if (!socket) {
      return;
    }

    /**
     * Handle socket connect event
     * @returns
     */
    const handleConnect = () => {
      const sessionId = getSession("sessionId");
      if (!sessionId) {
        return;
      }
      socket.emit("authenticate", { sessionId });
      setIsConnected(true);
    };

    /**
     * Handle socket disconnect event
     * @returns
     */
    const handleDisconnect = () => {
      logger.log("[socket] disconnected");
      setIsConnected(false);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    if (!socket.connected) {
      socket.connect();
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, [socket]);

  return (
    <SocketContext.Provider value={{ socket, isConnected, isEnabled }}>
      {children}
    </SocketContext.Provider>
  );
};

/**
 * useSocketContext
 * @returns SocketContextValue
 */
export const useSocketContext = () => {
  const context = useContext(SocketContext);

  if (!context) {
    throw new Error("useSocketContext must be used within a SocketProvider.");
  }

  return context;
};
